/**
 * A/B Test Purchase Widget
 * 
 * Handles the new quantity + subscription checkbox interface
 * Integrates with existing form elements and maintains compatibility
 */

// Avoid redeclaration when the script is included multiple times (e.g., Replo proxy)
window.ABTestPurchaseWidget = window.ABTestPurchaseWidget || class ABTestPurchaseWidget {
  constructor() {
    this.widget = null;
    this.isInitialized = false;
    this.currentQuantityType = '2_months';
    this.isSubscriptionEnabled = false;
    
    // Bind methods to maintain context
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleSubscriptionToggle = this.handleSubscriptionToggle.bind(this);
    this.updatePriceDisplay = this.updatePriceDisplay.bind(this);
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupWidget());
    } else {
      this.setupWidget();
    }
  }

  setupWidget() {
    this.widget = document.getElementById('ab-test-purchase-widget');
    
    if (!this.widget) {
      console.log('AB Test Widget: Widget container not found');
      return;
    }

    console.log('AB Test Widget: Found widget, initializing...', this.widget);
    
    this.bindEventListeners();
    this.setInitialState();
    this.isInitialized = true;
    
    console.log('AB Test Widget: Initialized successfully');
  }

  bindEventListeners() {
    console.log('AB Test Widget: Binding event listeners...');
    
    // Quantity selection radio buttons
    const quantityRadios = this.widget.querySelectorAll('[name="ab_quantity_option"]');
    console.log('AB Test Widget: Found quantity radios:', quantityRadios.length);
    
    quantityRadios.forEach((radio, index) => {
      console.log(`AB Test Widget: Binding radio ${index}:`, radio.value);
      radio.addEventListener('change', this.handleQuantityChange);
    });

    // Subscription checkbox
    const subscriptionCheckbox = this.widget.querySelector('#ab_auto_refills');
    console.log('AB Test Widget: Found subscription checkbox:', !!subscriptionCheckbox);
    
    if (subscriptionCheckbox) {
      subscriptionCheckbox.addEventListener('change', this.handleSubscriptionToggle);
    }

    // Delivery frequency selector
    const deliverySelector = this.widget.querySelector('#ab-test-delivery-select');
    if (deliverySelector) {
      deliverySelector.addEventListener('change', (e) => {
        this.updateHiddenSellingPlan(e.target.value);
      });
    }

    // Listen for variant changes to update prices
    document.addEventListener('change', (e) => {
      if (e.target.closest('variant-picker') || e.target.matches('[name="id"]')) {
        setTimeout(() => {
          this.updatePriceDisplay();
          // Keep subscription checkbox in sync with variant's selling plan availability
          this.syncSubscriptionAvailability();
          // After unit prices are refreshed by js_product.js, re-render prices again
          this.updatePriceDisplay();
          // Ensure compare-at visibility respects current subscription state after variant switch
          this.setCompareAtVisible(this.isSubscriptionEnabled);
        }, 100);
      }
    });
    
    console.log('AB Test Widget: Event listeners bound successfully');
  }

  handleQuantityChange(event) {
    const selectedRadio = event.target;
    this.currentQuantityType = selectedRadio.value;
    
    console.log('AB Test Widget: Quantity changed to', this.currentQuantityType, 'Radio:', selectedRadio);
    
    // Update visual state
    this.updateQuantityVisualState(selectedRadio);
    
    // Update hidden quantity input
    const quantity = selectedRadio.dataset.quantity;
    console.log('AB Test Widget: Setting quantity to', quantity);
    this.updateHiddenQuantity(parseInt(quantity));
    
    // Update prices
    this.updatePriceDisplay();
    
    // Trigger variant change if needed (for 4 months = quantity 2)
    if (quantity === '2') {
      this.triggerVariantChange();
    }
  }

  handleSubscriptionToggle(event) {
    const isChecked = event.target.checked;
    this.isSubscriptionEnabled = isChecked;
    
    console.log('AB Test Widget: Subscription toggled to', isChecked ? 'enabled' : 'disabled');
    
    // Update visual state
    this.updateSubscriptionVisualState(isChecked);
    
    // Update hidden radio buttons
    this.updateHiddenPurchaseOptions(isChecked);
    
    // Update prices
    this.updatePriceDisplay();
    // Toggle compare-at visibility based on subscription state
    this.setCompareAtVisible(isChecked);
    
    // Show/hide subscription details
    // this.toggleSubscriptionDetails(isChecked);
  }

  updateQuantityVisualState(selectedRadio) {
    // Remove selected state from all options
    this.widget.querySelectorAll('.ab-test-option').forEach(option => {
      option.classList.remove('ab-test-option--selected', 'bg-[#F7F4ED]');
      const dot = option.querySelector('.ab-test-radio-dot');
      if (dot) {
        dot.style.transform = 'scale(0)';
      }
    });

    // Add selected state to current option
    const currentOption = selectedRadio.closest('.ab-test-option');
    if (currentOption) {
      currentOption.classList.add('ab-test-option--selected', 'bg-[#F7F4ED]');
      const dot = currentOption.querySelector('.ab-test-radio-dot');
      if (dot) {
        dot.style.transform = 'scale(1)';
      }
    }
  }

  updateSubscriptionVisualState(isEnabled) {
    const checkbox = this.widget.querySelector('#ab_auto_refills');
    const indicator = this.widget.querySelector('.ab-test-checkbox-indicator');
    const checkmark = this.widget.querySelector('.ab-test-checkmark');
    
    if (isEnabled) {
      indicator.style.backgroundColor = '#586A38';
      checkmark.style.opacity = '1';
    } else {
      indicator.style.backgroundColor = 'white';
      checkmark.style.opacity = '0';
    }
  }

  // Ensure the AB test checkbox respects subscription availability of the selected variant
  syncSubscriptionAvailability() {
    const subscriptionCheckbox = this.widget.querySelector('#ab_auto_refills');
    const subscrInput = document.getElementById('subscr');
    const oneTimeRadio = document.getElementById('one_time');
    const subWrapper = document.querySelector('.subscription_wrapper');
    const checkboxLabel = this.widget.querySelector('.ab-test-checkbox-wrapper');

    if (!subscriptionCheckbox) return;

    // Determine availability from the legacy subscription radio's disabled state
    const subscriptionAvailable = subscrInput ? !subscrInput.disabled : true;

    if (!subscriptionAvailable) {
      // Disable AB test checkbox and force one-time purchase
      subscriptionCheckbox.checked = false;
      subscriptionCheckbox.disabled = true;
      this.isSubscriptionEnabled = false;
      this.updateSubscriptionVisualState(false);
      this.updateHiddenPurchaseOptions(false);
      if (subWrapper) subWrapper.classList.add('hidden');
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // Clear any selling plan selection
      this.updateHiddenSellingPlan('');

      // Dim the checkbox UI
      if (checkboxLabel) checkboxLabel.style.opacity = '0.5';
      // Hide discount adornments in the card
      const discountText = this.widget.querySelector('.ab-test-discount-text');
      if (discountText) discountText.style.display = 'none';
      this.widget.querySelectorAll('.ab-test-price-original').forEach(el => { el.style.visibility = 'hidden'; el.setAttribute('aria-hidden','true'); });
    } else {
      // Enable AB test checkbox when subscriptions are available
      subscriptionCheckbox.disabled = false;
      // Ensure a valid selling plan is selected in the hidden select when available
      const planSelect = document.getElementById('subscr_select');
      if (planSelect && (!planSelect.value || planSelect.value === '')) {
        const firstValid = Array.from(planSelect.options).find(o => o.value);
        if (firstValid) {
          this.updateHiddenSellingPlan(firstValid.value);
        }
      }
      // Restore UI visuals
      if (checkboxLabel) checkboxLabel.style.opacity = '1';
      const discountText = this.widget.querySelector('.ab-test-discount-text');
      if (discountText) discountText.style.display = '';
      this.widget.querySelectorAll('.ab-test-price-original').forEach(el => { el.style.visibility = 'visible'; el.removeAttribute('aria-hidden'); try { el.setAttribute('aria-label','Original price'); } catch(e) {} });
    }
  }

  updateHiddenQuantity(quantity) {
    const quantityInput = document.getElementById('quantity');
    if (quantityInput) {
      quantityInput.value = quantity;
      // Trigger change event to notify other systems
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateHiddenPurchaseOptions(isSubscription) {
    const subscriptionRadio = document.getElementById('subscr');
    const oneTimeRadio = document.getElementById('one_time');
    
    if (isSubscription && subscriptionRadio && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
      subscriptionRadio.dispatchEvent(new Event('change'));
    } else if (!isSubscription && oneTimeRadio && !oneTimeRadio.checked) {
      oneTimeRadio.checked = true;
      oneTimeRadio.dispatchEvent(new Event('change'));
    }
  }

  updateHiddenSellingPlan(sellingPlanId) {
    const sellingPlanInput = document.querySelector('[name="selling_plan"]');
    const hiddenSelector = document.getElementById('subscr_select');
    
    if (sellingPlanInput) {
      sellingPlanInput.value = sellingPlanId || '';
    }
    
    if (hiddenSelector) {
      hiddenSelector.value = sellingPlanId || '';
    }
  }

  updatePriceDisplay() {
    const quantityRadios = this.widget.querySelectorAll('[name="ab_quantity_option"]');

    // Prefer live unit prices provided by js_product.js over static data attributes
    const unitPrices = (window._igOriginalPrices || {});
    const unitOneTime = typeof unitPrices.oneTime === 'number' ? unitPrices.oneTime : null; // dollars
    const unitSub = typeof unitPrices.subscription === 'number' ? unitPrices.subscription : null; // dollars

    quantityRadios.forEach(radio => {
      const priceSpan = radio.closest('.ab-test-option').querySelector('.ab-test-price');
      if (!priceSpan) return;

      const sets = parseInt(radio.dataset.quantity || '1', 10) || 1;

      let cents;
      if (unitOneTime !== null && unitSub !== null) {
        const unit = this.isSubscriptionEnabled ? unitSub : unitOneTime; // dollars
        cents = Math.round(unit * 100 * sets);
      } else {
        // Fallback to static attributes (cents) if live prices aren't available yet
        const priceKey = this.isSubscriptionEnabled ? 'subscriptionPrice' : 'oneTimePrice';
        const raw = parseInt(radio.dataset[priceKey] || radio.dataset.oneTimePrice || '0', 10);
        cents = (isNaN(raw) ? 0 : raw);
      }

      priceSpan.textContent = this.formatPrice(cents);
      try { priceSpan.setAttribute('aria-label', this.isSubscriptionEnabled ? 'Discounted price' : 'Price'); } catch(e) {}
    });
  }

  // Show/hide the strikethrough compare price in the widget while preserving layout
  setCompareAtVisible(show) {
    this.widget.querySelectorAll('.ab-test-price-original').forEach(el => {
      el.style.visibility = show ? 'visible' : 'hidden';
    });
  }

  toggleSubscriptionDetails(show) {
    const details = document.getElementById('ab-test-subscription-details');
    if (details) {
      if (show) {
        details.classList.remove('hidden');
      } else {
        details.classList.add('hidden');
      }
    }
  }

  triggerVariantChange() {
    // This might be needed if switching to 4 months requires a different variant
    // For now, we just dispatch a general change event
    const event = new CustomEvent('ab_test_quantity_changed', {
      bubbles: true,
      detail: {
        quantityType: this.currentQuantityType,
        quantity: this.currentQuantityType === '2_sets_free_dish_hair_body_oil' ? 2 : 1
      }
    });
    document.dispatchEvent(event);
  }

  formatPrice(priceInCents) {
    // Simple price formatting - adjust based on your shop's currency format
    const price = priceInCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }

  setInitialState() {
    // Set default state: 2 months supply with subscription OFF
    console.log('AB Test Widget: Setting initial state');
    
    // Ensure 2 months is selected
    const twoMonthsRadio = this.widget.querySelector('[value="1_set_free_dish"]');
    if (twoMonthsRadio && !twoMonthsRadio.checked) {
      twoMonthsRadio.checked = true;
      this.handleQuantityChange({ target: twoMonthsRadio });
    }
    
    // Ensure subscription respects availability for the selected variant
    this.syncSubscriptionAvailability();

    // Default is OFF: ensure underlying radios reflect one-time
    const oneTimeRadio = document.getElementById('one_time');
    if (oneTimeRadio) {
      oneTimeRadio.checked = true;
      oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // With subscription OFF initially, hide compare-at in the card
    this.setCompareAtVisible(false);
    
    // Update initial prices
    this.updatePriceDisplay();
    
    // Show subscription details by default
    // this.toggleSubscriptionDetails(true);
    
    // Set initial delivery frequency
    const deliverySelector = this.widget.querySelector('#ab-test-delivery-select');
    if (deliverySelector && deliverySelector.options.length > 0) {
      this.updateHiddenSellingPlan(deliverySelector.value);
    }
  }

  // Public method to get current state
  getCurrentState() {
    return {
      quantityType: this.currentQuantityType,
      quantity: this.currentQuantityType === '2_sets_free_dish_hair_body_oil' ? 2 : 1,
      isSubscription: this.isSubscriptionEnabled,
      isInitialized: this.isInitialized
    };
  }

  // Public method to manually trigger state update
  refresh() {
    if (this.isInitialized) {
      this.updatePriceDisplay();
    }
  }
}

// Initialize the widget when DOM is ready
function initializeABTestWidget() {
  if (document.getElementById('ab-test-purchase-widget')) {
    // Reuse existing instance if present, otherwise create one
    if (!window.ABTestWidget || !window.ABTestWidget.isInitialized) {
      window.ABTestWidget = new window.ABTestPurchaseWidget();
    } else {
      // If already initialized (e.g., script included twice), just refresh prices/UI
      try { window.ABTestWidget.refresh && window.ABTestWidget.refresh(); } catch (e) {}
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeABTestWidget);
} else {
  initializeABTestWidget();
}

// Also listen for dynamic content loading
document.addEventListener('content:loaded', initializeABTestWidget);

// Export for module systems if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = window.ABTestPurchaseWidget;
}